import React from "react";
import {
  getAthleteName,
  getAthleteInstitution,
  getAthleteInstitutionLogo,
  getBracketStatusProps,
  getVictoryTypeProps,
} from "../categorias.utils";
import InstitutionLogo from "../InstitutionLogo";

const CORNER_COLOR = {
  blue:  { dot: "bg-blue-600",  ring: "ring-blue-100" },
  white: { dot: "bg-slate-400", ring: "ring-slate-100" },
  red:   { dot: "bg-red-500",   ring: "ring-red-100"  },
};
const getCornerStyle = (corner) =>
  CORNER_COLOR[String(corner || "").toLowerCase()] ?? CORNER_COLOR.white;

const ParticipantSlot = ({ entry, isWin, align = "left" }) => {
  const name    = getAthleteName(entry?.athlete);
  const inst    = getAthleteInstitution(entry?.athlete);
  const logoUrl = getAthleteInstitutionLogo(entry?.athlete);
  const cs      = getCornerStyle(String(entry?.corner || "").toLowerCase());
  const isRight = align === "right";

  return (
    <div
      className={`
        flex-1 px-3 py-2.5 rounded-xl border transition-all duration-200
        flex items-center gap-3
        ${isRight ? "flex-row-reverse" : "flex-row"}
        ${isWin
          ? "bg-green-500/5 border-green-400/30"
          : "bg-black/[0.02] border-gray-200"}
      `}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ring-4 ${cs.dot} ${cs.ring}`} />
      <InstitutionLogo logoUrl={logoUrl} name={inst} size={30} showTooltip={!!inst} />
      <div className={`flex-1 min-w-0 ${isRight ? "text-right" : "text-left"}`}>
        <div className={`flex items-center gap-1 ${isRight ? "justify-end" : "justify-start"}`}>
          {isWin && <span className="text-amber-400 text-xs flex-shrink-0">🏆</span>}
          <span className={`text-sm truncate ${isWin ? "font-bold text-green-600" : "font-medium text-gray-800"}`}>
            {name}
          </span>
        </div>
        <span className="block text-[0.68rem] text-gray-400 truncate tracking-wide">{inst}</span>
      </div>
    </div>
  );
};

const SeriesScore = ({ seriesScore }) => {
  if (!seriesScore) return null;
  const { participant1Wins: w1, participant2Wins: w2 } = seriesScore;
  const isDraw = w1 === w2;

  return (
    <div className="flex items-center justify-center gap-3 px-6 py-3 bg-black/[0.03] border-b border-gray-200">
      <span className={`text-4xl font-extrabold tabular-nums leading-none
        ${!isDraw && w1 > w2 ? "text-green-600" : "text-gray-800"}`}>
        {w1}
      </span>
      <span className="text-xl font-light text-gray-300 select-none">—</span>
      <span className={`text-4xl font-extrabold tabular-nums leading-none
        ${!isDraw && w2 > w1 ? "text-green-600" : "text-gray-800"}`}>
        {w2}
      </span>
      <span className="text-[0.68rem] uppercase tracking-widest text-gray-400 ml-1">victorias</span>
    </div>
  );
};

const RoundRow = ({ round, p1, p2 }) => {
  const s1        = round.scores?.participant1?.score ?? "—";
  const s2        = round.scores?.participant2?.score ?? "—";
  const rWinnerId = round.winner?.registrationId;
  const isP1Win   = rWinnerId === p1?.registrationId;
  const isP2Win   = rWinnerId === p2?.registrationId;

  return (
    <div
      className="grid items-center gap-3 px-3 py-2 rounded-xl bg-black/[0.02] border border-gray-200
                 hover:border-indigo-400 hover:bg-indigo-500/[0.04] transition-all duration-150"
      style={{ gridTemplateColumns: "36px 1fr auto 1fr" }}
    >
      <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-100 flex-shrink-0">
        <span className="text-[0.65rem] font-bold text-gray-500">R{round.roundNumber}</span>
      </div>
      <span className={`text-right text-[0.75rem] truncate
        ${isP1Win ? "font-bold text-green-600" : "font-normal text-gray-500"}`}>
        {getAthleteName(p1)}
      </span>
      <div className="flex items-center gap-1.5 flex-shrink-0 px-3 py-0.5 rounded-lg bg-gray-100">
        <span className={`text-sm tabular-nums w-6 text-right
          ${isP1Win ? "font-extrabold text-green-600" : "font-normal text-gray-400"}`}>
          {s1}
        </span>
        <span className="text-gray-300 text-sm select-none">·</span>
        <span className={`text-sm tabular-nums w-6 text-left
          ${isP2Win ? "font-extrabold text-green-600" : "font-normal text-gray-400"}`}>
          {s2}
        </span>
      </div>
      <span className={`text-[0.75rem] truncate
        ${isP2Win ? "font-bold text-green-600" : "font-normal text-gray-500"}`}>
        {getAthleteName(p2)}
      </span>
    </div>
  );
};

const BracketHeader = ({ matchNumber, label, right }) => (
  <div className="px-5 py-2.5 flex justify-between items-center border-b border-gray-200 bg-black/[0.03]">
    <div className="flex items-center gap-3">
      <div className="w-0.5 h-5 rounded-full bg-blue-600 flex-shrink-0" />
      <span className="text-[0.82rem] font-bold text-gray-800">{label} · Combate #{matchNumber}</span>
    </div>
    {right}
  </div>
);

const VistaLlaves = ({ brackets }) => (
  <div className="flex flex-col gap-5 p-1">
    {brackets.map((bracket) => {
      const [p1Entry, p2Entry] = bracket.participants || [];
      const seriesScore        = bracket.seriesScore;
      const statusProps        = getBracketStatusProps(bracket.status);
      const victoryProps       = bracket.victoryType ? getVictoryTypeProps(bracket.victoryType) : null;

      return (
        <div
          key={bracket.seriesId ?? bracket.bracketId}
          className="rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
        >
          <BracketHeader
            matchNumber={bracket.matchNumber}
            label="Serie Mejor de 3"
            right={
              <div className="flex items-center gap-1.5">
                {victoryProps && (
                  <span
                    className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded border ${victoryProps.color}`}
                    title={victoryProps.title}
                  >
                    {victoryProps.label}
                  </span>
                )}
                <span className="text-[0.65rem] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-black/[0.06] text-gray-500">
                  {statusProps.label}
                </span>
              </div>
            }
          />

          <SeriesScore seriesScore={seriesScore} />

          <div className="flex flex-col sm:flex-row items-stretch gap-3 p-4">
            <ParticipantSlot
              entry={p1Entry}
              isWin={seriesScore ? seriesScore.participant1Wins > seriesScore.participant2Wins : false}
              align="left"
            />
            <div className="flex items-center justify-center px-1">
              <span className="text-[0.7rem] font-bold uppercase tracking-widest text-gray-400">vs</span>
            </div>
            <ParticipantSlot
              entry={p2Entry}
              isWin={seriesScore ? seriesScore.participant2Wins > seriesScore.participant1Wins : false}
              align="right"
            />
          </div>

          {bracket.rounds?.length > 0 && (
            <div className="px-4 pb-4">
              <hr className="border-gray-200 mb-3" />
              <span className="block text-[0.65rem] font-bold uppercase tracking-widest text-gray-400 mb-2">
                Desglose por rondas
              </span>
              <div className="flex flex-col gap-1.5">
                {bracket.rounds.map((round) => (
                  <RoundRow
                    key={round.roundNumber}
                    round={round}
                    p1={p1Entry?.athlete}
                    p2={p2Entry?.athlete}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const VistaResultados = ({ brackets }) => (
  <div className="flex flex-col gap-5 p-1">
    {brackets.map((bracket) => {
      const [p1Entry, p2Entry] = bracket.participants || [];
      const p1                 = p1Entry?.athlete;
      const p2                 = p2Entry?.athlete;
      const p1Name             = getAthleteName(p1);
      const p2Name             = getAthleteName(p2);
      const seriesScore        = bracket.seriesScore;
      const victoryProps       = bracket.victoryType ? getVictoryTypeProps(bracket.victoryType) : null;

      return (
        <div
          key={bracket.seriesId}
          className="rounded-2xl border border-gray-200 overflow-hidden"
        >
          <BracketHeader
            matchNumber={bracket.matchNumber}
            label="Combate"
            right={
              <div className="flex items-center gap-1.5">
                {victoryProps && (
                  <span
                    className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded border ${victoryProps.color}`}
                    title={victoryProps.title}
                  >
                    {victoryProps.label}
                  </span>
                )}
                {seriesScore && (
                  <div className="flex items-center gap-1.5 px-3 py-0.5 rounded-lg bg-blue-600">
                    <span className="text-sm font-extrabold text-white tabular-nums tracking-widest">
                      {seriesScore.participant1Wins} — {seriesScore.participant2Wins}
                    </span>
                  </div>
                )}
              </div>
            }
          />

          {bracket.rounds?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-black/[0.02] border-b border-gray-200">
                    <th className="pl-5 pr-3 py-2.5 text-left text-[0.68rem] font-bold uppercase tracking-wide text-gray-400">
                      Ronda
                    </th>
                    <th className="px-3 py-2.5 text-center text-[0.68rem] font-bold uppercase tracking-wide text-gray-400">
                      <div className="flex items-center justify-center gap-1.5">
                        <InstitutionLogo logoUrl={getAthleteInstitutionLogo(p1Entry?.athlete)} name={getAthleteInstitution(p1Entry?.athlete)} size={16} showTooltip />
                        <span>{p1Name}</span>
                      </div>
                    </th>
                    <th className="px-3 py-2.5 text-center text-[0.68rem] font-bold uppercase tracking-wide text-gray-400">
                      <div className="flex items-center justify-center gap-1.5">
                        <InstitutionLogo logoUrl={getAthleteInstitutionLogo(p2Entry?.athlete)} name={getAthleteInstitution(p2Entry?.athlete)} size={16} showTooltip />
                        <span>{p2Name}</span>
                      </div>
                    </th>
                    <th className="pl-3 pr-5 py-2.5 text-center text-[0.68rem] font-bold uppercase tracking-wide text-gray-400">
                      Ganador
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bracket.rounds.map((round) => {
                    const s1         = round.scores?.participant1?.score ?? "—";
                    const s2         = round.scores?.participant2?.score ?? "—";
                    const rWinnerId  = round.winner?.registrationId;
                    const isP1Win    = rWinnerId === p1?.registrationId;
                    const isP2Win    = rWinnerId === p2?.registrationId;
                    const winnerName = isP1Win ? p1Name : isP2Win ? p2Name : "—";

                    return (
                      <tr
                        key={round.roundNumber}
                        className="border-b border-gray-100 hover:bg-black/[0.02] transition-colors"
                      >
                        <td className="pl-5 pr-3 py-2.5">
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
                            <span className="text-[0.68rem] font-bold text-gray-500">{round.roundNumber}</span>
                          </div>
                        </td>
                        <td className={`px-3 py-2.5 text-center tabular-nums ${isP1Win ? "font-bold text-green-600" : "text-gray-700"}`}>
                          {s1}
                        </td>
                        <td className={`px-3 py-2.5 text-center tabular-nums ${isP2Win ? "font-bold text-green-600" : "text-gray-700"}`}>
                          {s2}
                        </td>
                        <td className="pl-3 pr-5 py-2.5 text-center">
                          {rWinnerId ? (
                            <span className="text-[0.65rem] font-bold px-2 py-0.5 rounded bg-green-500/10 text-green-600">
                              {winnerName}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {seriesScore && (
                    <tr className="bg-black/[0.03] border-t-2 border-gray-200">
                      <td className="pl-5 pr-3 py-2.5">
                        <span className="text-[0.68rem] font-bold uppercase tracking-widest text-gray-400">Serie</span>
                      </td>
                      <td className={`px-3 py-2.5 text-center text-base font-extrabold tabular-nums
                        ${seriesScore.participant1Wins > seriesScore.participant2Wins ? "text-green-600" : "text-gray-500"}`}>
                        {seriesScore.participant1Wins}
                      </td>
                      <td className={`px-3 py-2.5 text-center text-base font-extrabold tabular-nums
                        ${seriesScore.participant2Wins > seriesScore.participant1Wins ? "text-green-600" : "text-gray-500"}`}>
                        {seriesScore.participant2Wins}
                      </td>
                      <td className="pl-3 pr-5 py-2.5" />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const FaseMejorDe3 = ({ fase, vista }) => {
  if (!fase?.brackets?.length) {
    return (
      <div className="m-4 flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
        <span>ℹ️</span>
        <span>No hay combates registrados en esta fase.</span>
      </div>
    );
  }

  return vista === "resultados" ? (
    <VistaResultados brackets={fase.brackets} />
  ) : (
    <VistaLlaves brackets={fase.brackets} />
  );
};

export default FaseMejorDe3;